import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { db } from '../../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { 
  FaUtensils, 
  FaTag, 
  FaEuroSign, 
  FaFileAlt, 
  FaImage, 
  FaList, 
  FaPlus, 
  FaCheck, 
  FaTimes, 
  FaSpinner, 
  FaUpload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaCog
} from 'react-icons/fa';
import './AddDishForm.css';

const AddDishForm = ({ menuId }) => {
  const [nom, setNom] = useState('');
  const [prix, setPrix] = useState('');
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState(null);
  const [categorie, setCategorie] = useState('');
  const [erreur, setErreur] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSupplementsPopup, setShowSupplementsPopup] = useState(false);
  const [suppléments, setSuppléments] = useState({});
  const [selectedSuppléments, setSelectedSuppléments] = useState({});

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowSupplementsPopup(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const fetchSupplements = async () => {
      try {
        const supplementRef = doc(db, 'menu', menuId);
        const docSnapshot = await getDoc(supplementRef);

        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setSuppléments(data.suppléments || {});
          
          const initialSelected = Object.keys(data.suppléments || {}).reduce((acc, category) => {
            acc[category] = [];
            return acc;
          }, {});
          setSelectedSuppléments(initialSelected);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des suppléments:', error);
        setSuppléments({});
        setSelectedSuppléments({});
      }
    };

    fetchSupplements();
  }, [menuId]);

  const handleSupplementChange = (category, supplement) => {
    setSelectedSuppléments((prevSelected) => {
      const prevCategorySelection = prevSelected[category] || [];
  
      const newSelection = prevCategorySelection.includes(supplement)
        ? prevCategorySelection.filter(item => item !== supplement)
        : [...prevCategorySelection, supplement];
  
      return {
        ...prevSelected,
        [category]: newSelection
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!nom.trim() || !prix || !description.trim() || !categorie) {
      setErreur('Tous les champs sont requis, sauf la photo.');
      return;
    }
  
    if (isNaN(parseFloat(prix)) || parseFloat(prix) < 0) {
      setErreur('Le prix doit être un nombre positif.');
      return;
    }
  
    setErreur('');
    setUploading(true);
  
    try {
      let photoURL = '';
  
      if (photo) {
        const formData = new FormData();
        formData.append('file', photo);
        formData.append('upload_preset', 'menu_photos');
        formData.append('cloud_name', 'dyuyrwww1');
  
        const response = await axios.post(
          `https://api.cloudinary.com/v1_1/dyuyrwww1/image/upload`,
          formData
        );
  
        if (!response.data.secure_url) {
          throw new Error("L'upload de l'image a échoué.");
        }
  
        photoURL = response.data.secure_url;
      }
  
      const idNom = nom.replace(/\s+/g, '_').toLowerCase();
  
      const updatedSelectedSupplements = Object.entries(selectedSuppléments).reduce(
        (acc, [category, selected]) => {
          if (selected.length > 0) {
            acc[category] = {
              items: selected,
              selectionType: suppléments[category]?.selectionType || "multiple",
            };
          }
          return acc;
        },
        {}
      );
  
      await setDoc(doc(db, `menu/${menuId}/${categorie}`, idNom), {
        nom: nom.trim(),
        prix: parseFloat(prix),
        description: description.trim(),
        photo: photoURL || null,
        categorie,
        actif: true,
        suppléments: updatedSelectedSupplements,
        createdAt: new Date(),
      });
  
      triggerPopup();
      resetForm();
      handleResetSupplements();
    } catch (error) {
      console.error('Erreur lors de l\'ajout du plat :', error.message);
      setErreur("Échec de l'ajout du plat. Vérifiez votre connexion ou Cloudinary.");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setNom('');
    setPrix('');
    setDescription('');
    setPhoto(null);
    setCategorie('');
  };

  const triggerPopup = () => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 2000);
  };

  const handleResetSupplements = () => {
    const resetSelected = Object.keys(suppléments).reduce((acc, category) => {
      acc[category] = [];
      return acc;
    }, {});
    setSelectedSuppléments(resetSelected);
  };

  const handlePrixChange = (e) => {
    const value = e.target.value;
    if (!isNaN(value) && value >= 0) {
      setPrix(value);
    }
    if (/^\d*\.?\d*$/.test(value)) {
      setPrix(value); 
    }
  };

  return (
    <div className="add-dish-container">
      {/* Header */}
      <div className="add-dish-header">
        <div className="header-content">
          <h1 className="add-dish-title">
            <FaUtensils className="title-icon" />
            Ajouter un Nouveau Plat
          </h1>
          <p className="add-dish-subtitle">
            Créez un nouveau plat pour votre menu avec tous les détails nécessaires
          </p>
        </div>
      </div>

      {/* Formulaire principal */}
      <div className="add-dish-form-card">
        {erreur && (
          <div className="error-message">
            <FaExclamationTriangle className="error-icon" />
            <span>{erreur}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="add-dish-form">
          {/* Section Informations de base */}
          <div className="form-section">
            <h3 className="section-title">
              <FaTag className="section-icon" />
              Informations de Base
            </h3>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FaUtensils className="label-icon" />
                  Nom du plat
                </label>
                <input
                  type="text"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className="form-input"
                  placeholder="Ex: Pizza Margherita"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <FaEuroSign className="label-icon" />
                  Prix (€)
                </label>
                <input
                  type="number"
                  value={prix}
                  onChange={handlePrixChange}
                  min="0"
                  step="0.01"
                  className="form-input"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FaFileAlt className="label-icon" />
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="form-textarea"
                placeholder="Décrivez votre plat, ses ingrédients et ses particularités..."
                rows="4"
                required
              />
            </div>
          </div>

          {/* Section Photo et Catégorie */}
          <div className="form-section">
            <h3 className="section-title">
              <FaImage className="section-icon" />
              Photo et Catégorie
            </h3>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <FaUpload className="label-icon" />
                  Photo du plat
                </label>
                <div className="file-upload-container">
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/*"
                    onChange={(e) => setPhoto(e.target.files[0])}
                    className="file-input"
                  />
                  <label htmlFor="photo-upload" className="file-upload-label">
                    <span className="upload-text">
                      {photo ? photo.name : "Sélectionner une photo"}
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <FaList className="label-icon" />
                  Catégorie
                </label>
                <select
                  value={categorie}
                  onChange={(e) => setCategorie(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Sélectionnez une catégorie</option>
                  <option value="entrees">Entrée</option>
                  <option value="plats">Plat</option>
                  <option value="desserts">Dessert</option>
                  <option value="boissons">Boisson</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section Suppléments */}
          <div className="form-section">
            <h3 className="section-title">
              <FaCog className="section-icon" />
              Suppléments
            </h3>
            
            {/* Affichage des suppléments sélectionnés */}
            {Object.entries(selectedSuppléments).map(([category, items]) => 
              items.length > 0 && (
                <div key={category} className="selected-supplements">
                  <div className="supplement-category">
                    <strong>{category}:</strong>
                    <div className="supplement-items">
                      {items.map((item, index) => (
                        <span key={index} className="supplement-tag">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )
            )}

            <button 
              type="button" 
              className="add-supplements-btn"
              onClick={() => setShowSupplementsPopup(true)}
            >
              <FaPlus className="btn-icon" />
              {Object.values(selectedSuppléments).some(items => items.length > 0) 
                ? 'Modifier les suppléments' 
                : 'Ajouter des suppléments'
              }
            </button>
          </div>

          {/* Bouton de soumission */}
          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <FaSpinner className="btn-icon spinning" />
                  Téléchargement en cours...
                </>
              ) : (
                <>
                  <FaCheck className="btn-icon" />
                  Ajouter le plat
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Popup de sélection des suppléments */}
      {showSupplementsPopup && (
        <div className="supplements-popup-overlay" onClick={() => setShowSupplementsPopup(false)}>
          <div className="supplements-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3 className="popup-title">
                <FaCog className="popup-icon" />
                Sélectionner des Suppléments
              </h3>
              <button 
                className="popup-close-btn"
                onClick={() => setShowSupplementsPopup(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="popup-content">
              {Object.entries(suppléments).map(([category, categoryData]) => (
                categoryData.items && categoryData.items.length > 0 && (
                  <div key={category} className="supplement-category-section">
                    <h4 className="category-title">
                      {category}
                      <span className="selection-type">
                        ({categoryData.selectionType === 'single' ? 'Choix unique' : 'Choix multiple'})
                      </span>
                    </h4>
                    <div className="supplement-options">
                      {categoryData.items.map((supplement) => (
                        <div className="supplement-option" key={supplement}>
                          <input
                            type='checkbox'
                            id={`supplement-${category}-${supplement}`}
                            name={categoryData.selectionType === 'single' ? `supplement-${category}` : undefined}
                            checked={selectedSuppléments[category]?.includes(supplement)}
                            onChange={() => handleSupplementChange(category, supplement)}
                            className="supplement-checkbox"
                          />
                          <label htmlFor={`supplement-${category}-${supplement}`} className="supplement-label">
                            {supplement}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
            
            <div className="popup-actions">
              <button 
                type='button' 
                className="reset-btn"
                onClick={handleResetSupplements}
              >
                <FaTimes className="btn-icon" />
                Réinitialiser
              </button>
              <button 
                className="validate-btn"
                onClick={() => setShowSupplementsPopup(false)}
              >
                <FaCheck className="btn-icon" />
                Valider
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmation */}
      {showPopup && (
        <div className="success-popup">
          <div className="success-content">
            <FaCheckCircle className="success-icon" />
            <span>Plat ajouté avec succès !</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDishForm;